import { Fragment, useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import axiosInstance from "../../../services/AxiosInstance";
import Api from "../../../constants/Api";
import PageTitle from "../../layouts/PageTitle";
import LoadingCenter from "../../components/Common/LoadingCenter";
import { ApiError } from "../../components/Common/ApiError";
import EmployeeSelect from "../../components/Custom/Main/Employee/EmployeeSelect";
import FinancialSummaryDocument from "./FinancialSummaryDocument";
import { useBranch } from "../../../context/BranchContext";
import '../../components/Common/Report/ReportFilters.css';

/** O acerto costuma ser quinzenal ou mensal, então os atalhos cobrem esses fechamentos. */
const QUICK_PERIODS = {
    this_month: () => [moment().startOf('month'), moment().endOf('month')],
    last_month: () => [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
    first_half: () => [moment().startOf('month'), moment().startOf('month').add(14, 'days')],
    second_half: () => [moment().startOf('month').add(15, 'days'), moment().endOf('month')],
};

const QUICK_PERIOD_LABELS = {
    this_month: 'Mês atual',
    last_month: 'Mês anterior',
    first_half: '1ª quinzena',
    second_half: '2ª quinzena',
};

const toApiDate = (date) => date.format('YYYY-MM-DD');

/**
 * Resumo financeiro do profissional em tela própria: escolhe período e colaborador e o
 * documento de acerto sai pronto para imprimir e assinar.
 *
 * O mesmo documento é acessível pelo detalhe do extrato do colaborador. Os dois caminhos
 * renderizam FinancialSummaryDocument, então o papel entregue é idêntico.
 */
const EmployeeFinancialSummaryReport = () => {

    const { t } = useTranslation();

    // A filial ativa vem do cabeçalho, como nas demais telas de relatório.
    const { selectedBranch } = useBranch();
    const branchId = selectedBranch?.id ?? selectedBranch?.value ?? null;

    const [employee, setEmployee] = useState(null);
    const [period, setPeriod] = useState(() => {
        const [start, end] = QUICK_PERIODS.this_month();
        return { startDate: toApiDate(start), endDate: toApiDate(end) };
    });
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);

    const employeeId = employee?.id ?? employee?.value ?? null;
    const invalidPeriod = moment(period.endDate).isBefore(moment(period.startDate));

    const load = useCallback(() => {
        if (!employeeId || invalidPeriod) {
            setDetail(null);
            return;
        }

        setLoading(true);
        axiosInstance.get(`${Api.reports.employee_statement}/${employeeId}`, { params: { ...period, branchId } })
            .then(({ data }) => setDetail(data))
            .catch((error) => { setDetail(null); ApiError(error); })
            .finally(() => setLoading(false));
    }, [employeeId, period, branchId, invalidPeriod]);

    useEffect(() => { load(); }, [load]);

    const applyQuickPeriod = (key) => {
        const [start, end] = QUICK_PERIODS[key]();
        setPeriod({ startDate: toApiDate(start), endDate: toApiDate(end) });
    };

    const isQuickPeriodActive = (key) => {
        const [start, end] = QUICK_PERIODS[key]();
        return period.startDate === toApiDate(start) && period.endDate === toApiDate(end);
    };

    // O mesmo recorte que o extrato faz: item sem comissão possível não entra na lista que
    // paga, e o tipo separa serviço de produto.
    const commissionItems = (detail?.commissionItems || []).filter(item => item.commissionVeto !== true);
    const typedServiceItems = commissionItems.filter(item =>
        String(item.product?.type || '').toUpperCase() === 'SERVICE');
    const serviceItems = typedServiceItems.length > 0 || commissionItems.length === 0
        ? typedServiceItems
        : commissionItems;
    const productItems = commissionItems.filter(item =>
        String(item.product?.type || '').toUpperCase() === 'PRODUCT');

    return (
        <Fragment>
            <PageTitle activeMenu={t('employee_financial_summary')} motherMenu={t('reports')} />

            <div className="report-filters">
                <Row className="g-3 align-items-end">
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('employee')}</Form.Label>
                            <EmployeeSelect
                                branch={selectedBranch}
                                value={employee}
                                onChange={(option) => setEmployee(option)}
                                filterProvidesService={false}
                            />
                        </Form.Group>
                    </Col>
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('date_start')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={period.startDate}
                                onChange={(event) => setPeriod(current => ({ ...current, startDate: event.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('date_end')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={period.endDate}
                                onChange={(event) => setPeriod(current => ({ ...current, endDate: event.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    {/* Atalhos abaixo dos campos, ocupando a linha: mesmo desenho do extrato
                        do colaborador, para o filtro não mudar de forma a cada relatório.
                        Aqui entram também as quinzenas, que é como o acerto é fechado. */}
                    <Col xs={12}>
                        <div className="report-filters__quick-periods">
                            {Object.keys(QUICK_PERIODS).map(key => (
                                <Button
                                    key={key}
                                    size="sm"
                                    variant={isQuickPeriodActive(key) ? 'primary' : 'outline-primary'}
                                    onClick={() => applyQuickPeriod(key)}
                                >
                                    {QUICK_PERIOD_LABELS[key]}
                                </Button>
                            ))}
                        </div>
                    </Col>
                </Row>

                {invalidPeriod && (
                    <div className="report-filters__warning">
                        <i className="fas fa-triangle-exclamation me-2"></i>
                        {t('validation.period_start_after_end')}
                    </div>
                )}
            </div>

            <div className="card">
                <div className="card-body">
                    {loading ? <LoadingCenter /> : !employeeId ? (
                        <p className="report-empty">Selecione o colaborador para gerar o resumo do período.</p>
                    ) : !detail ? (
                        <p className="report-empty">Nenhum lançamento encontrado no período selecionado.</p>
                    ) : (
                        <FinancialSummaryDocument
                            detail={detail}
                            period={period}
                            serviceItems={serviceItems}
                            productItems={productItems}
                        />
                    )}
                </div>
            </div>
        </Fragment>
    );
};

export default EmployeeFinancialSummaryReport;
